interface DomainMeta {
  environment: 'dev' | 'stage' | 'uat' | 'prod';
  monolithOrigin: string;
  tenant: string;
  recruiterUiOrigin: string;
  launchDarklyClientSideId: string;
}

// Searches for any domain such as source.ap-1.fountain.com. Will not match `source.fountain.com`.
const FOUNTAIN_DOMAIN_MATCHER = /^source\.[^.]*\.fountain\.com/;
// Matches the staging domains such as source.staging-use-05.fountain.com
const FOUNTAIN_STAGING_DOMAIN_MATCHER = /^source\.staging[^.]*\.fountain\.com/;
const RWX_SANDBOX_DOMAIN_MATCHER =
  /^source-ui-(.+)--fountain\.rwx\.run$/;

const getLaunchDarklyClientSideId = (
  environment: DomainMeta['environment'],
): string => {
  switch (environment) {
    case 'prod':
      return '652f17e244cb56124fa8cf96';
    case 'dev':
      return '65c66efbfc74a80ff1860f04';
    default:
      return '652f17e244cb56124fa8cf95';
  }
};

const FOUNTAIN_DEV_DOMAINS = new Set([
  'source.dev-01.fountain.com',
  'source.dev-02.fountain.com',
  'source.faut-02.fountain.com',
  'source.faut-03.fountain.com',
]);

const FOUNTAIN_UAT_DOMAINS = new Set([
  'source.uat-01.fountain.com',
  'source.faut-01.fountain.com',
]);

const isStagingDomain = (hostname: string) =>
  FOUNTAIN_STAGING_DOMAIN_MATCHER.test(hostname);

const isUatDomain = (hostname: string) => FOUNTAIN_UAT_DOMAINS.has(hostname);

const isDevDomain = (hostname: string) => FOUNTAIN_DEV_DOMAINS.has(hostname);

const getMonolithOrigin = (hostname: string) =>
  `https://${hostname.replace('source.', '')}`;

const getRecruiterUiOrigin = (hostname: string) =>
  `https://${hostname.replace('source', 'app')}`;

const getTenant = (hostname: string) => hostname.split('.')[1];

const buildMeta = (
  environment: DomainMeta['environment'],
  hostname: string,
): DomainMeta => ({
  environment,
  monolithOrigin: getMonolithOrigin(hostname),
  tenant: getTenant(hostname),
  recruiterUiOrigin: getRecruiterUiOrigin(hostname),
  launchDarklyClientSideId: getLaunchDarklyClientSideId(environment),
});

export const inferDomainMeta = (hostname: string): DomainMeta => {
  if (hostname === 'localhost') {
    return {
      environment: 'dev',
      monolithOrigin: `http://localhost:${
        process.env.NODE_ENV === 'test' ? 3023 : 3000
      }`,
      tenant: 'local',
      recruiterUiOrigin: 'http://localhost:3333',
      launchDarklyClientSideId: getLaunchDarklyClientSideId('dev'),
    };
  }

  const rwxSandbox = hostname.match(RWX_SANDBOX_DOMAIN_MATCHER);
  if (rwxSandbox) {
    const slug = rwxSandbox[1];

    return {
      environment: 'dev',
      monolithOrigin: `https://hire-${slug}--fountain.rwx.run`,
      tenant: 'local',
      recruiterUiOrigin: `https://recruiter-ui-${slug}--fountain.rwx.run`,
      launchDarklyClientSideId: getLaunchDarklyClientSideId('dev'),
    };
  }

  if (isDevDomain(hostname)) return buildMeta('dev', hostname);
  if (isStagingDomain(hostname)) return buildMeta('stage', hostname);
  if (isUatDomain(hostname)) return buildMeta('uat', hostname);

  if (hostname === 'source.fountain.com') {
    return {
      environment: 'prod',
      monolithOrigin: 'https://web.fountain.com',
      tenant: 'fountain',
      recruiterUiOrigin: getRecruiterUiOrigin(hostname),
      launchDarklyClientSideId: getLaunchDarklyClientSideId('prod'),
    };
  }

  if (FOUNTAIN_DOMAIN_MATCHER.test(hostname)) {
    return buildMeta('prod', hostname);
  }

  throw new Error('Unable to map hostname to a known domain');
};

export const domainMeta = inferDomainMeta(window.location.hostname);
